import { CloseIcon, ResetIcon } from '@blocksuite/icons/lit';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { endOfDay } from 'date-fns/endOfDay';
import { format } from 'date-fns/format';
import { getISOWeek } from 'date-fns/getISOWeek';
import { isSameDay } from 'date-fns/isSameDay';
import { max } from 'date-fns/max';
import { min } from 'date-fns/min';
import { startOfDay } from 'date-fns/startOfDay';
import { html, nothing, type TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

import {
  dataViewI18nT,
  getDataViewRuntimeLocale,
} from '../../../../core/i18n.js';
import type { CalendarOccurrence } from '../../calendar-domain.js';
import { minutesSinceStartOfDay } from '../../core/calendar-time-math.js';
import type { CalendarIcsSubscription, CalendarMode } from '../../define.js';
import {
  allDayGridColumn,
  layoutTimedEvents,
  timedEventGeometry,
  type TimedEventLayout,
} from '../../layout/calendar-event-geometry.js';
import {
  type CalendarDisplaySettings,
  calendarEventAccessibleText,
  calendarEventDisplayValues,
  calendarSourceIdentityVisible,
} from '../calendar-display-policy.js';
import { CALENDAR_SURFACE_HOUR_HEIGHT } from '../calendar-surface-range.js';
import { styles } from '../calendar-view-styles.js';
import type {
  CalendarDragOffset,
  CalendarEvent,
  CalendarEventEditPreview,
  CalendarRangeLoading,
  CalendarSlotDrag,
  CalendarSubscriptionState,
} from '../calendar-view-types.js';
import { pointerMinuteWithinDay } from '../calendar-view-utils.js';

const CALENDAR_SLOT_CREATE_MINUTES = 15;
const CALENDAR_MINUTES_PER_DAY = 24 * 60;
export const CALENDAR_ALL_DAY_RENDER_PAGE_SIZE = 12;

export type CalendarTimedRenderWindow = Readonly<{
  endMinute: number;
  startMinute: number;
}>;

export type CalendarTimedViewport = Readonly<{
  renderWindow: CalendarTimedRenderWindow;
  visibleBottom: number;
  visibleTop: number;
}>;

type CalendarPointerSession = {
  onPointerCancel: (pointer: PointerEvent) => void;
  onPointerMove: (pointer: PointerEvent) => void;
  onPointerUp: (pointer: PointerEvent) => void;
};

function noop(): void {}

function startCalendarPointerSession(
  target: HTMLElement,
  pointerId: number,
  session: CalendarPointerSession
): () => void {
  if (typeof target.setPointerCapture === 'function') {
    target.setPointerCapture(pointerId);
  }

  window.addEventListener('pointermove', session.onPointerMove);
  window.addEventListener('pointerup', session.onPointerUp);
  window.addEventListener('pointercancel', session.onPointerCancel);

  return () => {
    window.removeEventListener('pointermove', session.onPointerMove);
    window.removeEventListener('pointerup', session.onPointerUp);
    window.removeEventListener('pointercancel', session.onPointerCancel);
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(pointerId) &&
      typeof target.releasePointerCapture === 'function'
    ) {
      target.releasePointerCapture(pointerId);
    }
  };
}

export type CalendarGridRenderContext = {
  allDayCollapsed: boolean;
  allDayRenderLimit: number;
  allDayEventsForDays: (
    days: readonly Date[]
  ) => CalendarOccurrence<CalendarEvent>[];
  calendarIds: readonly string[];
  cursor: Date;
  displaySettings: CalendarDisplaySettings;
  draggedOccurrence: CalendarOccurrence<CalendarEvent> | undefined;
  draggedOccurrenceOffset: CalendarDragOffset | undefined;
  dragCreateSlot: CalendarSlotDrag | undefined;
  eventEditPreview: CalendarEventEditPreview | undefined;
  eventsForDay: (day: Date) => CalendarOccurrence<CalendarEvent>[];
  finishSlotDrag: (day: Date, endMinute: number) => void;
  mode: CalendarMode;
  now: Date;
  onClearDragCreateSlot: () => void;
  onCreateEvent: (day?: Date) => void;
  onDragCreateSlot: (day: Date, startMinute: number) => void;
  onPreviewDragCreateSlot: (day: Date, endMinute: number) => void;
  onDraggedOccurrenceChange: (
    occurrence: CalendarOccurrence<CalendarEvent> | undefined,
    offset?: CalendarDragOffset
  ) => void;
  onClearOccurrencePreview: () => void;
  onCommitOccurrenceMove: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date,
    startMinute: number
  ) => void;
  onCommitOccurrenceMoveToDay: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ) => void;
  onCommitOccurrenceMoveToAllDay: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ) => void;
  onEventKeyDown: (
    key: KeyboardEvent,
    event: CalendarOccurrence<CalendarEvent>
  ) => void;
  onEventContextMenu: (
    menu: MouseEvent,
    event: CalendarOccurrence<CalendarEvent>
  ) => void;
  onMoveOccurrenceToDay: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ) => void;
  onOpenDay: (day: Date) => void;
  onOpenWeek: (day: Date) => void;
  onRefreshIcsSubscription: (subscription: CalendarIcsSubscription) => void;
  onRemoveIcsSubscription: (subscriptionId: string) => void;
  onRenameOccurrence: (
    event: CalendarOccurrence<CalendarEvent>,
    title: string
  ) => void;
  onSelectOccurrence: (event: CalendarOccurrence<CalendarEvent>) => void;
  onShowMoreAllDayEvents: (total: number) => void;
  onSetIcsSubscriptionEnabled: (
    subscriptionId: string,
    enabled: boolean
  ) => void;
  onToggleAllDayCollapsed: () => void;
  onSetVisibleCalendarIds: (ids: readonly string[]) => void;
  onPreviewOccurrenceMove: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date,
    startMinute: number
  ) => void;
  onPreviewOccurrenceMoveToDay: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ) => void;
  onPreviewOccurrenceMoveToAllDay: (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ) => void;
  onPreviewOccurrenceResize: (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date
  ) => void;
  onCommitOccurrenceResize: (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date
  ) => void;
  onStartResizePointer: (
    pointer: PointerEvent,
    event: CalendarOccurrence<CalendarEvent>,
    edge: 'end' | 'start'
  ) => void;
  rangeLoading: CalendarRangeLoading | undefined;
  readonly: boolean;
  recurrenceErrors: ReadonlyMap<string, string>;
  selectedOccurrenceStart: string | undefined;
  selectedRowId: string | undefined;
  subscriptionStates: Readonly<Record<string, CalendarSubscriptionState>>;
  subscriptions: readonly CalendarIcsSubscription[];
  timedOffscreen: Readonly<{ bottom: number; top: number }>;
  timedRenderWindow: CalendarTimedRenderWindow;
  visibleCalendarIds: readonly string[];
  onJumpToHiddenTimedEvents: (side: 'bottom' | 'top') => void;
};

export function renderCalendarGrid(
  context: CalendarGridRenderContext,
  days: readonly Date[]
): TemplateResult {
  if (context.mode === 'day') {
    return html`<div
      class="${styles.dayMode}"
      data-data-view-body-render-root="true"
    >
      ${renderTimedCalendarSurface(context, days)}
    </div>`;
  }
  if (context.mode === 'week') {
    return html`<div
      class="${styles.weekSurface}"
      data-data-view-body-render-root="true"
    >
      <div class="${styles.weekScroll}" data-calendar-week-scroll>
        ${renderRangeLoading(context, 'week')}
        <div
          class="${styles.weekCanvas}"
          style=${`--calendar-day-count:${Math.max(1, days.length)}`}
        >
          ${renderTimedCalendarSurface(context, days, true)}
        </div>
      </div>
    </div>`;
  }
  return html`<div
    class="${styles.monthScroll}"
    data-calendar-month-scroll
    data-data-view-body-render-root="true"
  >
    ${renderRangeLoading(context, 'month')}
    <div class="${styles.monthGrid}">
      ${repeat(
        days,
        day => day.getTime(),
        day => html`
          ${day.getDay() === 1 ? renderMonthWeekButton(context, day) : nothing}
          ${renderDayColumn(context, day)}
        `
      )}
    </div>
  </div>`;
}

function renderMonthWeekButton(
  context: CalendarGridRenderContext,
  weekStart: Date
): TemplateResult {
  const week = getISOWeek(weekStart);
  return html`<button
    class="${styles.monthWeekButton}"
    data-calendar-week=${week}
    type="button"
    aria-label=${dataViewI18nT('calendar.accessibility.switchMode', {
      mode: `${dataViewI18nT('calendar.mode.week')} ${week}`,
    })}
    @click=${() => context.onOpenWeek(weekStart)}
  >
    <small>KW</small><strong>${week}</strong>
  </button>`;
}

function renderTimedCalendarSurface(
  context: CalendarGridRenderContext,
  days: readonly Date[],
  externalScroll = false
): TemplateResult {
  return html`
    ${renderTimeGridHeader(context, days)}
    <div class="${styles.timeGridFrame}">
      ${renderTimeGrid(context, days, externalScroll)}
      ${renderTimedOffscreenIndicator(context, 'top')}
      ${renderTimedOffscreenIndicator(context, 'bottom')}
    </div>
  `;
}

function renderRangeLoading(
  context: CalendarGridRenderContext,
  mode: 'month' | 'week'
): TemplateResult | typeof nothing {
  if (context.rangeLoading?.mode !== mode) return nothing;
  return html`<div
    class="${styles.rangeLoading}"
    data-direction=${context.rangeLoading.direction}
    data-mode=${mode}
  >
    ${dataViewI18nT('calendar.grid.loading')}
  </div>`;
}

export function renderCalendarOverlays(
  context: CalendarGridRenderContext
): TemplateResult | typeof nothing {
  const ids = context.calendarIds;
  const subscriptions = context.subscriptions;
  if (ids.length <= 1 && !subscriptions.length) return nothing;
  const enabled = context.visibleCalendarIds.length
    ? new Set(context.visibleCalendarIds)
    : new Set(ids);
  return html`<div class="${styles.overlayBar}">
    ${ids.length > 1
      ? repeat(
          ids,
          id => id,
          id =>
            html`<label class="${styles.overlayToggle}">
              <input
                type="checkbox"
                aria-label=${dataViewI18nT(
                  'calendar.accessibility.toggleCalendar',
                  {
                    name: id,
                  }
                )}
                .checked=${enabled.has(id)}
                ?disabled=${context.readonly}
                @change=${(change: Event) => {
                  const input = change.currentTarget as HTMLInputElement;
                  const next = new Set(enabled);
                  if (input.checked) next.add(id);
                  else next.delete(id);
                  context.onSetVisibleCalendarIds(
                    next.size === ids.length ? [] : [...next]
                  );
                }}
              />
              <span>${id}</span>
            </label>`
        )
      : nothing}
    ${repeat(
      subscriptions,
      subscription => subscription.id,
      subscription => {
        const state = context.subscriptionStates[subscription.id];
        const enabled = subscription.enabled !== false;
        return html`<span
          class="${styles.subscriptionChip}"
          data-error=${Boolean(state?.error)}
        >
          <label>
            <input
              type="checkbox"
              aria-label=${dataViewI18nT('calendar.accessibility.toggleFeed', {
                name: subscription.name || subscription.url,
              })}
              .checked=${enabled}
              ?disabled=${context.readonly}
              @change=${(change: Event) =>
                context.onSetIcsSubscriptionEnabled(
                  subscription.id,
                  (change.currentTarget as HTMLInputElement).checked
                )}
            />
            <span>${subscription.name || subscription.url}</span>
          </label>
          <button
            class="${styles.inlineIconButton}"
            aria-label=${dataViewI18nT('calendar.accessibility.refreshFeed', {
              name: subscription.name || subscription.url,
            })}
            ?disabled=${!enabled}
            @click=${() => context.onRefreshIcsSubscription(subscription)}
          >
            ${ResetIcon({ height: '14px', width: '14px' })}
          </button>
          <button
            class="${styles.inlineIconButton}"
            aria-label=${dataViewI18nT('calendar.accessibility.removeFeed', {
              name: subscription.name || subscription.url,
            })}
            ?disabled=${context.readonly}
            @click=${() => context.onRemoveIcsSubscription(subscription.id)}
          >
            ${CloseIcon({ height: '14px', width: '14px' })}
          </button>
          ${state?.loading
            ? html`<small>${dataViewI18nT('calendar.grid.loading')}</small>`
            : state?.error
              ? html`<small>${state.error}</small>`
              : nothing}
        </span>`;
      }
    )}
  </div>`;
}

function formatCalendarDate(
  date: Date,
  options: Intl.DateTimeFormatOptions
): string {
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), options).format(
    date
  );
}

function formatCalendarDateTime(date: Date): string {
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function renderDayHeaders(
  context: CalendarGridRenderContext,
  days: readonly Date[],
  includeTimeAxis = false
): TemplateResult {
  return html`<div
    class="${styles.dayHeaders}"
    data-time-axis=${includeTimeAxis}
    style=${`--calendar-day-count:${Math.max(1, days.length)}`}
  >
    ${includeTimeAxis
      ? html`<div
          class="${styles.dayHeaderSpacer}"
          data-calendar-sticky-column="header"
        ></div>`
      : nothing}
    ${repeat(
      days,
      day => day.getTime(),
      day =>
        html`<button
          class="${styles.dayHeader}"
          aria-label=${dataViewI18nT('calendar.accessibility.openDay', {
            date: formatCalendarDate(day, { dateStyle: 'medium' }),
          })}
          data-today=${isSameDay(day, context.now)}
          @click=${() => context.onOpenDay(day)}
        >
          <span>${formatCalendarDate(day, { weekday: 'short' })}</span>
          <strong>${formatCalendarDate(day, { day: 'numeric' })}</strong>
          ${day.getDay() === 1
            ? html`<small class="${styles.weekNumberPill}"
                >KW ${getISOWeek(day)}</small
              >`
            : nothing}
        </button>`
    )}
  </div>`;
}

function renderDayColumn(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult {
  const events = context.eventsForDay(day);
  const visibleEvents = context.mode === 'month' ? events.slice(0, 4) : events;
  const hiddenCount = Math.max(0, events.length - visibleEvents.length);
  return html`<section
    class="${context.mode === 'month' ? styles.monthCell : styles.weekCell}"
    data-calendar-day=${day.getTime()}
    data-data-view-body-render-content="true"
    data-calendar-month-anchor=${day.getDate() === 1
      ? monthAnchorValue(day)
      : nothing}
    data-calendar-drop-target="day"
    data-outside-month=${context.mode === 'month' &&
    day.getMonth() !== context.cursor.getMonth()}
    data-today=${isSameDay(day, context.now)}
    @pointerdown=${(pointer: PointerEvent) => {
      if (context.mode !== 'month' || context.readonly || pointer.button !== 0)
        return;
      if (
        pointer.target instanceof HTMLElement &&
        pointer.target.closest('button, [data-calendar-event]')
      )
        return;
      startCalendarDayRangeCreatePointer(context, pointer, day);
    }}
    @click=${(event: MouseEvent) => {
      if (context.mode !== 'month') return;
      const target = event.currentTarget as HTMLElement;
      if (target.dataset.calendarSuppressClick === 'true') return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('button, [data-calendar-event]')
      )
        return;
      context.onOpenDay(day);
    }}
  >
    <div class="${styles.cellDate}">
      <span
        >${formatCalendarDate(
          day,
          context.mode === 'month'
            ? { day: 'numeric' }
            : { day: 'numeric', weekday: 'short' }
        )}</span
      >
      <button
        class="${styles.cellAddButton}"
        aria-label=${dataViewI18nT('calendar.accessibility.createEventOn', {
          date: formatCalendarDate(day, { dateStyle: 'medium' }),
        })}
        ?disabled=${context.readonly}
        @click=${(event: Event) => {
          event.stopPropagation();
          context.onCreateEvent(day);
        }}
      >
        +
      </button>
    </div>
    <div class="${styles.eventStack}">
      ${repeat(visibleEvents, occurrenceKey, event =>
        renderEvent(context, event, day)
      )}
      ${renderDayRangeCreatePreview(context, day)}
      ${renderDayDragGhost(context, day)}
      ${hiddenCount
        ? html`<button
            class="${styles.moreEventsButton}"
            aria-label=${dataViewI18nT(
              'calendar.accessibility.openMoreEvents',
              {
                count: hiddenCount,
                date: formatCalendarDate(day, { dateStyle: 'medium' }),
              }
            )}
            @click=${(event: Event) => {
              event.stopPropagation();
              context.onOpenDay(day);
            }}
          >
            ${dataViewI18nT('calendar.grid.moreEvents', {
              count: hiddenCount,
            })}
          </button>`
        : nothing}
    </div>
  </section>`;
}

function renderTimeGrid(
  context: CalendarGridRenderContext,
  days: readonly Date[],
  externalScroll = false
): TemplateResult {
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  return html`<div
    class="${styles.timeGrid}"
    data-external-scroll=${externalScroll}
    data-calendar-time-scroll
    style=${`--calendar-day-count:${Math.max(1, days.length)}`}
  >
    <div class="${styles.timeAxis}" data-calendar-sticky-column="time-axis">
      ${repeat(
        hours,
        hour => hour,
        hour =>
          html`<div class="${styles.timeAxisHour}">
            ${hour.toString().padStart(2, '0')}:00
          </div>`
      )}
    </div>
    <div class="${styles.timeGridContent}">
      <div class="${styles.timeGridDays}">
        ${repeat(
          days,
          day => day.getTime(),
          day => renderTimeGridDay(context, day)
        )}
      </div>
    </div>
  </div>`;
}

function renderTimedOffscreenIndicator(
  context: CalendarGridRenderContext,
  side: 'bottom' | 'top'
): TemplateResult | typeof nothing {
  const count =
    side === 'top' ? context.timedOffscreen.top : context.timedOffscreen.bottom;
  if (!count) return nothing;
  const labelKey =
    side === 'top'
      ? 'calendar.accessibility.jumpToEarlierEvents'
      : 'calendar.accessibility.jumpToLaterEvents';
  const titleKey =
    side === 'top'
      ? 'calendar.grid.hiddenEarlier'
      : 'calendar.grid.hiddenLater';
  return html`<button
    class="${styles.timedOffscreenIndicator}"
    data-calendar-offscreen=${side}
    type="button"
    aria-label=${dataViewI18nT(labelKey, { count })}
    title=${dataViewI18nT(titleKey, { count })}
    @click=${() => context.onJumpToHiddenTimedEvents(side)}
  >
    <span aria-hidden="true">${side === 'top' ? '↑' : '↓'}</span>
    <strong>${count}</strong>
  </button>`;
}

function renderTimeGridHeader(
  context: CalendarGridRenderContext,
  days: readonly Date[]
): TemplateResult {
  return html`<div
    class="${styles.timeGridHeader}"
    style=${`--calendar-day-count:${Math.max(1, days.length)}`}
  >
    ${renderDayHeaders(context, days, true)}
    <div
      class="${styles.allDayHeader}"
      data-collapsed=${context.allDayCollapsed}
    >
      <div
        class="${styles.allDayLabel}"
        data-collapsed=${context.allDayCollapsed}
        data-calendar-sticky-column="all-day"
      >
        <button
          class="${styles.allDayToggle}"
          aria-label=${dataViewI18nT(
            context.allDayCollapsed
              ? 'calendar.accessibility.expandAllDay'
              : 'calendar.accessibility.collapseAllDay'
          )}
          aria-expanded=${!context.allDayCollapsed}
          @click=${(event: Event) => {
            event.stopPropagation();
            context.onToggleAllDayCollapsed();
          }}
        >
          <span title=${dataViewI18nT('calendar.grid.allDay')}>24h</span>
          <i
            class="${styles.allDayCollapseIcon}"
            data-collapsed=${context.allDayCollapsed}
            aria-hidden="true"
          ></i>
        </button>
      </div>
      ${renderAllDayBand(context, days)}
    </div>
  </div>`;
}

function renderAllDayBand(
  context: CalendarGridRenderContext,
  days: readonly Date[]
): TemplateResult {
  const events = context.allDayEventsForDays(days);
  const visibleEvents = selectCalendarAllDayEventsForRender(
    events,
    {
      draggedOccurrence: context.draggedOccurrence,
      selectedOccurrenceStart: context.selectedOccurrenceStart,
      selectedRowId: context.selectedRowId,
    },
    context.allDayRenderLimit
  );
  const visibleKeys = new Set(visibleEvents.map(occurrenceKey));
  const hiddenEvents = events.filter(
    event => !visibleKeys.has(occurrenceKey(event))
  );
  const firstVisibleDay = days[0] ?? context.cursor;
  const allDayDropActive = Boolean(
    context.draggedOccurrence && !context.draggedOccurrence.source.allDay
  );
  return html`<div
    class="${styles.allDayBand}"
    data-collapsed=${context.allDayCollapsed}
    data-calendar-day-count=${Math.max(1, days.length)}
    data-single-day=${days.length <= 1}
    data-calendar-drop-target="all-day"
    data-calendar-range-start=${startOfDay(firstVisibleDay).getTime()}
    data-drop-active=${allDayDropActive}
    data-testid="calendar-all-day-band"
    @dblclick=${() => context.onCreateEvent(days[0] ?? context.cursor)}
  >
    <div class="${styles.daySeparatorGrid}" aria-hidden="true">
      ${repeat(
        days,
        day => day.getTime(),
        () => html`<i></i>`
      )}
    </div>
    ${context.allDayCollapsed
      ? nothing
      : repeat(visibleEvents, occurrenceKey, event => {
          const firstVisibleDay = days[0] ?? event.startAt;
          const lastVisibleDay = days.at(-1) ?? firstVisibleDay;
          const visibleSpan = Math.max(
            1,
            differenceInCalendarDays(
              min([event.endAt, endOfDay(lastVisibleDay)]),
              max([event.startAt, startOfDay(firstVisibleDay)])
            ) + 1
          );
          return renderEvent(
            context,
            event,
            max([startOfDay(event.startAt), startOfDay(firstVisibleDay)]),
            {
              derivePointerDayOffset: true,
              pointerDaySpan: visibleSpan,
              style: allDayGridColumn(event.startAt, event.endAt, days),
            }
          );
        })}
    ${!context.allDayCollapsed && hiddenEvents.length
      ? html`<button
          class="${styles.moreEventsButton}"
          style="grid-column:1 / -1"
          aria-label=${dataViewI18nT('calendar.accessibility.openMoreEvents', {
            count: hiddenEvents.length,
            date: formatCalendarDate(firstVisibleDay, {
              dateStyle: 'medium',
            }),
          })}
          data-calendar-all-day-more
          @click=${(event: Event) => {
            event.stopPropagation();
            context.onShowMoreAllDayEvents(events.length);
          }}
        >
          ${dataViewI18nT('calendar.grid.moreEvents', {
            count: hiddenEvents.length,
          })}
        </button>`
      : nothing}
    ${renderAllDayDragGhost(context, days)}
  </div>`;
}

export function selectCalendarAllDayEventsForRender(
  events: readonly CalendarOccurrence<CalendarEvent>[],
  selection: Readonly<{
    draggedOccurrence: CalendarOccurrence<CalendarEvent> | undefined;
    selectedOccurrenceStart: string | undefined;
    selectedRowId: string | undefined;
  }>,
  limit = CALENDAR_ALL_DAY_RENDER_PAGE_SIZE
): CalendarOccurrence<CalendarEvent>[] {
  const draggedKey = selection.draggedOccurrence
    ? occurrenceKey(selection.draggedOccurrence)
    : undefined;
  const pinned = events.filter(
    event =>
      occurrenceKey(event) === draggedKey ||
      (event.source.rowId === selection.selectedRowId &&
        event.startAt.toISOString() === selection.selectedOccurrenceStart)
  );
  const pinnedKeys = new Set(pinned.map(occurrenceKey));
  return [
    ...pinned,
    ...events.filter(event => !pinnedKeys.has(occurrenceKey(event))),
  ].slice(0, Math.max(limit, pinned.length));
}

export function nextCalendarAllDayRenderLimit(
  current: number,
  total: number
): number {
  return Math.min(total, current + CALENDAR_ALL_DAY_RENDER_PAGE_SIZE);
}

function renderTimeGridDay(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult {
  const events = context.eventsForDay(day);
  const timedEvents = filterCalendarTimedEventsForRenderWindow(
    events,
    day,
    context.timedRenderWindow
  );
  const eventLayouts = layoutTimedEvents(
    timedEvents.map(event => ({
      endAt: event.endAt,
      id: occurrenceKey(event),
      startAt: event.startAt,
    }))
  );
  return html`<section class="${styles.timeGridDay}">
    <div
      class="${styles.hourGrid}"
      aria-label=${dataViewI18nT('calendar.accessibility.createEventOn', {
        date: formatCalendarDate(day, { dateStyle: 'medium' }),
      })}
      data-calendar-day=${day.getTime()}
      data-calendar-drop-target="time"
      data-data-view-body-render-content="true"
      role="gridcell"
      tabindex="-1"
      @pointerdown=${(pointer: PointerEvent) => {
        if (context.readonly || pointer.button !== 0) return;
        if (
          pointer.target instanceof HTMLElement &&
          pointer.target.closest('[data-calendar-event]')
        ) {
          return;
        }
        startCalendarSlotCreatePointer(context, pointer, day);
      }}
    >
      ${isSameDay(day, context.now)
        ? html`<div
            class="${styles.currentTimeLine}"
            style=${`--calendar-current-minute:${minutesSinceStartOfDay(
              context.now
            )}`}
          ></div>`
        : nothing}
      ${repeat(timedEvents, occurrenceKey, event =>
        renderTimedEvent(
          context,
          event,
          day,
          eventLayouts.get(occurrenceKey(event))
        )
      )}
      ${renderTimedDragGhost(context, day)}
      ${renderSlotDragPreview(context, day)}
    </div>
  </section>`;
}

export function filterCalendarTimedEventsForRenderWindow(
  events: readonly CalendarOccurrence<CalendarEvent>[],
  day: Date,
  window: CalendarTimedRenderWindow
): CalendarOccurrence<CalendarEvent>[] {
  return events.filter(event => {
    if (event.source.allDay) return false;
    const { heightMinute, startMinute } = timedEventGeometry(
      day,
      event.startAt,
      event.endAt
    );
    return (
      startMinute + heightMinute >= window.startMinute &&
      startMinute <= window.endMinute
    );
  });
}

export function calendarTimedViewport(
  scrollTop: number,
  clientHeight: number,
  contentOffsetTop: number,
  overscanMinutes: number
): CalendarTimedViewport {
  const visibleTop = Math.min(
    CALENDAR_MINUTES_PER_DAY,
    Math.max(
      0,
      (scrollTop - contentOffsetTop) / (CALENDAR_SURFACE_HOUR_HEIGHT / 60)
    )
  );
  const visibleBottom = Math.max(
    0,
    Math.min(
      CALENDAR_MINUTES_PER_DAY,
      (scrollTop + clientHeight - contentOffsetTop) /
        (CALENDAR_SURFACE_HOUR_HEIGHT / 60)
    )
  );
  return {
    renderWindow: {
      endMinute: Math.min(
        CALENDAR_MINUTES_PER_DAY,
        visibleBottom + overscanMinutes
      ),
      startMinute: Math.max(0, visibleTop - overscanMinutes),
    },
    visibleBottom,
    visibleTop,
  };
}

function formatMinuteLabel(minute: number): string {
  const normalized = Math.max(0, Math.min(CALENDAR_MINUTES_PER_DAY, minute));
  const hour = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${hour.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
}

function renderSlotDragPreview(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult | typeof nothing {
  const slot = context.dragCreateSlot;
  if (!slot || slot.allDay) return nothing;
  const start = startOfDay(slot.day).getTime() + slot.startMinute * 60_000;
  const end = startOfDay(slot.endDay).getTime() + slot.endMinute * 60_000;
  const first = Math.min(start, end);
  const last = Math.max(start, end) + CALENDAR_SLOT_CREATE_MINUTES * 60_000;
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + CALENDAR_MINUTES_PER_DAY * 60_000;
  if (last <= dayStart || first >= dayEnd) return nothing;
  const dayStartMinute = Math.max(0, (first - dayStart) / 60_000);
  const dayEndMinute = Math.min(
    CALENDAR_MINUTES_PER_DAY,
    (last - dayStart) / 60_000
  );
  return html`<div
    class="${styles.slotPreview}"
    style=${`--calendar-slot-start-minute:${dayStartMinute};--calendar-slot-duration-minute:${Math.max(
      CALENDAR_SLOT_CREATE_MINUTES,
      dayEndMinute - dayStartMinute
    )}`}
  >
    <span
      >${formatMinuteLabel(dayStartMinute)} -
      ${formatMinuteLabel(dayEndMinute)}</span
    >
  </div>`;
}

function renderDayRangeCreatePreview(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult | typeof nothing {
  const slot = context.dragCreateSlot;
  if (!slot?.allDay) return nothing;
  const first = Math.min(
    startOfDay(slot.day).getTime(),
    startOfDay(slot.endDay).getTime()
  );
  const last = Math.max(
    startOfDay(slot.day).getTime(),
    startOfDay(slot.endDay).getTime()
  );
  const current = startOfDay(day).getTime();
  if (current < first || current > last) return nothing;
  return html`<div class="${styles.daySlotPreview}" aria-hidden="true"></div>`;
}

function startCalendarDayRangeCreatePointer(
  context: CalendarGridRenderContext,
  pointer: PointerEvent,
  day: Date
): void {
  const target = pointer.currentTarget as HTMLElement;
  const startX = pointer.clientX;
  const startY = pointer.clientY;
  let dragging = false;
  let latestDay = day;
  let cleanup = noop;
  const onPointerMove = (move: PointerEvent) => {
    if (
      !dragging &&
      Math.hypot(move.clientX - startX, move.clientY - startY) < 4
    )
      return;
    if (!dragging) {
      dragging = true;
      target.dataset.calendarSuppressClick = 'true';
      context.onDragCreateSlot(day, 0);
    }
    const next = readCalendarPointerMoveTarget(move);
    if (!next) return;
    latestDay = next.day;
    context.onPreviewDragCreateSlot(latestDay, CALENDAR_MINUTES_PER_DAY - 1);
    move.preventDefault();
  };
  const onPointerUp = (up: PointerEvent) => {
    cleanup();
    if (!dragging) return;
    setTimeout(() => delete target.dataset.calendarSuppressClick, 0);
    up.preventDefault();
    context.finishSlotDrag(latestDay, CALENDAR_MINUTES_PER_DAY - 1);
  };
  const onPointerCancel = () => {
    cleanup();
    context.onClearDragCreateSlot();
  };
  cleanup = startCalendarPointerSession(target, pointer.pointerId, {
    onPointerCancel,
    onPointerMove,
    onPointerUp,
  });
}

function startCalendarSlotCreatePointer(
  context: CalendarGridRenderContext,
  pointer: PointerEvent,
  day: Date
): void {
  const target = pointer.currentTarget as HTMLElement;
  pointer.preventDefault();
  pointer.stopPropagation();
  const startMinute = pointerMinuteWithinDay(pointer, target);
  context.onDragCreateSlot(day, startMinute);
  let cleanup = noop;

  const onPointerMove = (move: PointerEvent) => {
    move.preventDefault();
    const next = readCalendarPointerMoveTarget(move);
    if (next?.kind !== 'time') return;
    context.onPreviewDragCreateSlot(
      next.day,
      pointerMinuteWithinDay(move, nextTargetElement(move) ?? target)
    );
  };

  const onPointerUp = (up: PointerEvent) => {
    cleanup();
    up.preventDefault();
    const next = readCalendarPointerMoveTarget(up);
    const endDay = next?.kind === 'time' ? next.day : day;
    const endMinute =
      next?.kind === 'time'
        ? pointerMinuteWithinDay(up, nextTargetElement(up) ?? target)
        : pointerMinuteWithinDay(up, target);
    context.onPreviewDragCreateSlot(endDay, endMinute);
    context.finishSlotDrag(endDay, endMinute);
  };

  const onPointerCancel = () => {
    cleanup();
    context.onClearDragCreateSlot();
  };

  cleanup = startCalendarPointerSession(target, pointer.pointerId, {
    onPointerCancel,
    onPointerMove,
    onPointerUp,
  });
}

function nextTargetElement(pointer: PointerEvent): HTMLElement | null {
  const element = document.elementFromPoint(pointer.clientX, pointer.clientY);
  return element instanceof HTMLElement
    ? element.closest<HTMLElement>('[data-calendar-drop-target="time"]')
    : null;
}

export type CalendarPointerMoveTarget =
  | {
      day: Date;
      kind: 'all-day';
    }
  | {
      day: Date;
      kind: 'day';
    }
  | {
      day: Date;
      kind: 'time';
      startMinute: number;
    };

export function readCalendarPointerMoveTarget(
  pointer: PointerEvent
): CalendarPointerMoveTarget | undefined {
  const element = document.elementFromPoint(pointer.clientX, pointer.clientY);
  const target =
    element instanceof HTMLElement
      ? element.closest<HTMLElement>('[data-calendar-drop-target]')
      : null;
  if (!target) return undefined;
  if (target.dataset.calendarDropTarget === 'all-day') {
    return {
      day: pointerDayWithinRangeTarget(target, pointer),
      kind: 'all-day',
    };
  }
  const dayTime = Number(target.dataset.calendarDay);
  if (!Number.isFinite(dayTime)) return undefined;
  const day = new Date(dayTime);
  return target.dataset.calendarDropTarget === 'time'
    ? {
        day,
        kind: 'time',
        startMinute: pointerMinuteWithinDay(pointer, target),
      }
    : {
        day,
        kind: 'day',
      };
}

function pointerDayWithinRangeTarget(
  target: HTMLElement,
  pointer: PointerEvent
): Date {
  const startTime = Number(target.dataset.calendarRangeStart);
  const firstDay = Number.isFinite(startTime)
    ? startOfDay(new Date(startTime))
    : startOfDay(new Date());
  const dayCount = Math.max(1, Number(target.dataset.calendarDayCount) || 1);
  const rect = target.getBoundingClientRect();
  const ratio = rect.width ? (pointer.clientX - rect.left) / rect.width : 0;
  const index = Math.max(
    0,
    Math.min(dayCount - 1, Math.floor(ratio * dayCount))
  );
  return addDays(firstDay, index);
}

function pointerMinuteOffsetWithinOccurrence(
  event: CalendarOccurrence<CalendarEvent>,
  target: HTMLElement,
  pointer: PointerEvent,
  sourceDay: Date
): number {
  const rect = target.getBoundingClientRect();
  const segmentRatio = rect.height
    ? (pointer.clientY - rect.top) / rect.height
    : 0;
  return calendarOccurrencePointerMinuteOffset(
    event.startAt,
    event.endAt,
    sourceDay,
    segmentRatio
  );
}

export function calendarOccurrencePointerMinuteOffset(
  eventStart: Date,
  eventEnd: Date,
  sourceDay: Date,
  segmentRatio: number
): number {
  // A multi-day timed occurrence is rendered as one clipped segment per day.
  // Derive the grab offset from the segment that was actually pressed, then
  // translate it back into the occurrence's full duration. Using the full
  // duration against a single segment's DOM height makes the event jump so
  // its midpoint sits under the pointer as soon as dragging starts.
  const segmentStart = max([eventStart, startOfDay(sourceDay)]);
  const segmentEnd = min([eventEnd, startOfDay(addDays(sourceDay, 1))]);
  const segmentStartOffset = Math.max(
    0,
    Math.round((segmentStart.getTime() - eventStart.getTime()) / 60_000)
  );
  const segmentDurationMinutes = Math.max(
    15,
    Math.round((segmentEnd.getTime() - segmentStart.getTime()) / 60_000)
  );
  const normalizedRatio = Math.max(0, Math.min(1, segmentRatio));
  const segmentPointerOffset =
    Math.round(
      (segmentDurationMinutes * normalizedRatio) / CALENDAR_SLOT_CREATE_MINUTES
    ) * CALENDAR_SLOT_CREATE_MINUTES;
  return segmentStartOffset + segmentPointerOffset;
}

function startCalendarOccurrenceMovePointer(
  context: CalendarGridRenderContext,
  pointer: PointerEvent,
  event: CalendarOccurrence<CalendarEvent>,
  sourceDay: Date
): void {
  const readonly = context.readonly || event.source.externalReadonly;
  if (readonly || pointer.button !== 0) return;
  pointer.preventDefault();
  pointer.stopPropagation();
  const target = pointer.currentTarget as HTMLElement;
  const startX = pointer.clientX;
  const startY = pointer.clientY;
  const pointerMinuteOffset =
    target.dataset.calendarTimedEvent !== 'true'
      ? 0
      : pointerMinuteOffsetWithinOccurrence(event, target, pointer, sourceDay);
  const pointerSourceDay =
    target.dataset.calendarDerivePointerDay === 'true'
      ? addDays(sourceDay, pointerDayOffsetWithinEvent(target, pointer))
      : sourceDay;
  const pointerDayOffset = differenceInCalendarDays(
    startOfDay(pointerSourceDay),
    startOfDay(event.startAt)
  );
  let dragging = false;
  let latestTarget: CalendarPointerMoveTarget | undefined;
  let cleanup = noop;

  const preview = (move: PointerEvent) => {
    latestTarget = readCalendarPointerMoveTarget(move);
    if (!latestTarget) return;
    if (latestTarget.kind === 'time') {
      context.onPreviewOccurrenceMove(
        event,
        addDays(latestTarget.day, -pointerDayOffset),
        latestTarget.startMinute - pointerMinuteOffset
      );
    } else if (latestTarget.kind === 'day') {
      context.onPreviewOccurrenceMoveToDay(
        event,
        addDays(latestTarget.day, -pointerDayOffset)
      );
    } else {
      context.onPreviewOccurrenceMoveToAllDay(
        event,
        addDays(latestTarget.day, -pointerDayOffset)
      );
    }
  };

  const onPointerMove = (move: PointerEvent) => {
    const distance = Math.hypot(move.clientX - startX, move.clientY - startY);
    if (!dragging && distance < 4) return;
    if (!dragging) {
      dragging = true;
      target.dataset.calendarSuppressClick = 'true';
    }
    context.onDraggedOccurrenceChange(event, {
      x: move.clientX - startX,
      y: move.clientY - startY,
    });
    move.preventDefault();
    preview(move);
  };

  const onPointerUp = (up: PointerEvent) => {
    cleanup();
    if (!dragging) return;
    setTimeout(() => delete target.dataset.calendarSuppressClick, 0);
    up.preventDefault();
    if (!latestTarget) {
      context.onClearOccurrencePreview();
      context.onDraggedOccurrenceChange(undefined);
      return;
    }
    if (latestTarget.kind === 'time') {
      context.onCommitOccurrenceMove(
        event,
        addDays(latestTarget.day, -pointerDayOffset),
        latestTarget.startMinute - pointerMinuteOffset
      );
    } else if (latestTarget.kind === 'day') {
      context.onCommitOccurrenceMoveToDay(
        event,
        addDays(latestTarget.day, -pointerDayOffset)
      );
    } else {
      context.onCommitOccurrenceMoveToAllDay(
        event,
        addDays(latestTarget.day, -pointerDayOffset)
      );
    }
    context.onDraggedOccurrenceChange(undefined);
  };

  const onPointerCancel = () => {
    cleanup();
    delete target.dataset.calendarSuppressClick;
    context.onClearOccurrencePreview();
    context.onDraggedOccurrenceChange(undefined);
  };

  cleanup = startCalendarPointerSession(target, pointer.pointerId, {
    onPointerCancel,
    onPointerMove,
    onPointerUp,
  });
}

function renderTimedEvent(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>,
  day: Date,
  layout: TimedEventLayout | undefined,
  options: {
    derivePointerDayOffset?: boolean;
    floating?: boolean;
    ghost?: boolean;
  } = {}
): TemplateResult {
  const selected =
    context.selectedRowId === event.source.rowId &&
    context.selectedOccurrenceStart === event.startAt.toISOString();
  const startsToday = isSameDay(event.startAt, day);
  const endsToday = isSameDay(event.endAt, day);
  const { startMinute, heightMinute } = timedEventGeometry(
    day,
    event.startAt,
    event.endAt
  );
  const durationMinute = Math.max(
    0,
    Math.round((event.endAt.getTime() - event.startAt.getTime()) / 60_000)
  );
  const density =
    durationMinute <= 30
      ? 'micro'
      : durationMinute <= 45
        ? 'compact'
        : 'regular';
  const readonly = context.readonly || event.source.externalReadonly;
  const invalid = context.recurrenceErrors.has(event.source.rowId);
  const dragging = isDraggedOccurrence(context, event);
  const ghost = options.ghost === true;
  const floating = ghost && options.floating === true;
  const previewing = isPreviewedOccurrence(context, event);
  const placeholder = (dragging || previewing) && !ghost;
  const display =
    ghost || placeholder ? event : displayOccurrence(context, event);
  return html`<div
    class="${styles.timedEvent}"
    role="button"
    tabindex="0"
    aria-label=${eventTitle(context, display)}
    data-calendar-event="true"
    data-calendar-event-key=${event.source.rowId}
    data-calendar-event-density=${density}
    data-calendar-timed-event="true"
    data-selected=${selected}
    data-dragging=${ghost}
    data-calendar-drag-floating=${floating}
    data-calendar-drag-ghost=${ghost}
    data-calendar-drag-placeholder=${placeholder}
    data-invalid=${invalid}
    data-invitation-preview=${event.source.invitationPreview === true}
    data-readonly=${readonly}
    title=${eventTitle(context, display)}
    style=${`--calendar-event-start-minute:${startMinute};--calendar-event-duration-minute:${heightMinute};--calendar-event-column:${layout?.column ?? 0};--calendar-event-columns:${layout?.columns ?? 1}${calendarEventColorStyle(event)}${floating ? calendarDragOffsetStyle(context) : ''}`}
    @pointerdown=${(pointer: PointerEvent) =>
      startCalendarOccurrenceMovePointer(context, pointer, event, day)}
    @click=${(click: Event) => {
      click.stopPropagation();
      if ((click.currentTarget as HTMLElement).dataset.calendarSuppressClick)
        return;
      context.onSelectOccurrence(event);
    }}
    @contextmenu=${(menu: MouseEvent) =>
      context.onEventContextMenu(menu, event)}
    @keydown=${(key: KeyboardEvent) => context.onEventKeyDown(key, event)}
  >
    ${readonly || (!startsToday && !options.derivePointerDayOffset)
      ? nothing
      : html`<button
            class="${styles.resizeHandle}"
            data-edge="start"
            aria-label=${dataViewI18nT('calendar.accessibility.resizeStart')}
            @pointerdown=${(pointer: PointerEvent) => {
              pointer.stopPropagation();
              context.onStartResizePointer(pointer, event, 'start');
            }}
          ></button>
          <button
            class="${styles.resizeHandle}"
            data-edge="end"
            aria-label=${dataViewI18nT('calendar.accessibility.resizeEnd')}
            @pointerdown=${(pointer: PointerEvent) => {
              pointer.stopPropagation();
              context.onStartResizePointer(pointer, event, 'end');
            }}
          ></button>`}
    ${readonly || context.mode !== 'week'
      ? nothing
      : html`<button
          class="${styles.dayResizeHandle}"
          data-edge="start"
          aria-label=${dataViewI18nT('calendar.accessibility.resizeStart')}
          @pointerdown=${(pointer: PointerEvent) =>
            startCalendarOccurrenceDayResizePointer(
              context,
              pointer,
              event,
              'start'
            )}
        ></button>`}
    ${readonly || (!endsToday && !options.derivePointerDayOffset)
      ? nothing
      : html`<button
          class="${styles.dayResizeHandle}"
          data-edge="end"
          aria-label=${dataViewI18nT('calendar.accessibility.resizeEnd')}
          @pointerdown=${(pointer: PointerEvent) =>
            startCalendarOccurrenceDayResizePointer(
              context,
              pointer,
              event,
              'end'
            )}
        ></button>`}
    <span class="${styles.timedEventPrimary}" data-calendar-event-primary>
      <span class="${styles.eventTime}"
        >${format(display.startAt, 'HH:mm')}</span
      >
      ${renderEditableTitle(context, event, 'strong')}
    </span>
    ${invalid
      ? html`<small>${dataViewI18nT('calendar.grid.invalidRecurrence')}</small>`
      : nothing}
    ${renderEventMetadata(context, display.source)}
    ${event.source.invitationPreview
      ? html`<small class="${styles.eventBadge}" data-calendar-event-badge
          >${dataViewI18nT('calendar.event.invitationPreview')}</small
        >`
      : nothing}
  </div>`;
}

function renderTimedDragGhost(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult | typeof nothing {
  const ghost = draggedGhostOccurrence(context);
  if (
    !ghost ||
    ghost.occurrence.source.allDay ||
    !occurrenceIntersectsDay(ghost.occurrence, day)
  ) {
    return nothing;
  }
  return renderTimedEvent(context, ghost.occurrence, day, undefined, {
    floating: ghost.floating,
    ghost: true,
  });
}

function renderAllDayDragGhost(
  context: CalendarGridRenderContext,
  days: readonly Date[]
): TemplateResult | typeof nothing {
  const ghost = draggedGhostOccurrence(context);
  if (!ghost?.occurrence.source.allDay || context.allDayCollapsed) {
    return nothing;
  }
  const occurrence = ghost.occurrence;
  const firstVisibleDay = days[0] ?? occurrence.startAt;
  const lastVisibleDay = days.at(-1) ?? firstVisibleDay;
  if (
    occurrence.startAt.getTime() > endOfDay(lastVisibleDay).getTime() ||
    occurrence.endAt.getTime() < startOfDay(firstVisibleDay).getTime()
  ) {
    return nothing;
  }
  const visibleSpan = Math.max(
    1,
    differenceInCalendarDays(
      min([occurrence.endAt, endOfDay(lastVisibleDay)]),
      max([occurrence.startAt, startOfDay(firstVisibleDay)])
    ) + 1
  );
  return renderEvent(
    context,
    occurrence,
    max([startOfDay(occurrence.startAt), startOfDay(firstVisibleDay)]),
    {
      derivePointerDayOffset: true,
      floating: ghost.floating,
      ghost: true,
      pointerDaySpan: visibleSpan,
      style: allDayGridColumn(occurrence.startAt, occurrence.endAt, days),
    }
  );
}

function renderDayDragGhost(
  context: CalendarGridRenderContext,
  day: Date
): TemplateResult | typeof nothing {
  const ghost = draggedGhostOccurrence(context);
  if (
    !ghost ||
    context.mode !== 'month' ||
    !occurrenceIntersectsDay(ghost.occurrence, day)
  ) {
    return nothing;
  }
  return renderEvent(context, ghost.occurrence, day, {
    floating: ghost.floating,
    ghost: true,
  });
}

function startCalendarOccurrenceDayResizePointer(
  context: CalendarGridRenderContext,
  pointer: PointerEvent,
  event: CalendarOccurrence<CalendarEvent>,
  edge: 'end' | 'start'
): void {
  if (
    context.readonly ||
    event.source.externalReadonly ||
    pointer.button !== 0
  ) {
    return;
  }
  pointer.preventDefault();
  pointer.stopPropagation();
  const target = pointer.currentTarget as HTMLElement;
  const initialStart = event.source.startAt;
  const initialEnd = event.source.endAt;
  let latestStart = initialStart;
  let latestEnd = initialEnd;
  let cleanup = noop;

  const preview = (move: PointerEvent) => {
    const day = readCalendarPointerMoveTarget(move)?.day;
    if (!day) return;
    if (edge === 'start') {
      const nextStart = addDays(
        initialStart,
        differenceInCalendarDays(day, initialStart)
      );
      if (nextStart.getTime() >= initialEnd.getTime()) return;
      latestStart = nextStart;
      latestEnd = initialEnd;
    } else {
      const nextEnd = addDays(
        initialEnd,
        differenceInCalendarDays(day, initialEnd)
      );
      if (nextEnd.getTime() <= initialStart.getTime()) return;
      latestStart = initialStart;
      latestEnd = nextEnd;
    }
    context.onPreviewOccurrenceResize(event, latestStart, latestEnd);
  };
  const onPointerMove = (move: PointerEvent) => {
    move.preventDefault();
    preview(move);
  };
  const onPointerUp = (up: PointerEvent) => {
    cleanup();
    up.preventDefault();
    preview(up);
    if (
      latestStart.getTime() === initialStart.getTime() &&
      latestEnd.getTime() === initialEnd.getTime()
    ) {
      context.onClearOccurrencePreview();
      return;
    }
    context.onCommitOccurrenceResize(event, latestStart, latestEnd);
  };
  const onPointerCancel = () => {
    cleanup();
    context.onClearOccurrencePreview();
  };
  cleanup = startCalendarPointerSession(target, pointer.pointerId, {
    onPointerCancel,
    onPointerMove,
    onPointerUp,
  });
}

function renderEvent(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>,
  day: Date,
  options: {
    derivePointerDayOffset?: boolean;
    floating?: boolean;
    ghost?: boolean;
    pointerDaySpan?: number;
    style?: string;
  } = {}
): TemplateResult {
  const selected =
    context.selectedRowId === event.source.rowId &&
    context.selectedOccurrenceStart === event.startAt.toISOString();
  const startsToday = isSameDay(event.startAt, day);
  const endsToday = isSameDay(event.endAt, day);
  const span = Math.max(
    1,
    differenceInCalendarDays(
      min([event.endAt, endOfDay(day)]),
      max([event.startAt, startOfDay(day)])
    ) + 1
  );
  const readonly = context.readonly || event.source.externalReadonly;
  const invalid = context.recurrenceErrors.has(event.source.rowId);
  const dragging = isDraggedOccurrence(context, event);
  const ghost = options.ghost === true;
  const floating = ghost && options.floating === true;
  const previewing = isPreviewedOccurrence(context, event);
  const placeholder = (dragging || previewing) && !ghost;
  const display =
    ghost || placeholder ? event : displayOccurrence(context, event);
  return html`<div
    class="${styles.eventChip}"
    role="button"
    tabindex="0"
    aria-label=${eventTitle(context, display)}
    data-calendar-derive-pointer-day=${options.derivePointerDayOffset === true}
    data-calendar-event="true"
    data-calendar-event-span=${options.pointerDaySpan ?? span}
    data-selected=${selected}
    data-dragging=${ghost}
    data-calendar-drag-floating=${floating}
    data-calendar-drag-ghost=${ghost}
    data-calendar-drag-placeholder=${placeholder}
    data-all-day=${event.source.allDay}
    data-invalid=${invalid}
    data-invitation-preview=${event.source.invitationPreview === true}
    data-readonly=${readonly}
    data-starts-today=${startsToday}
    data-ends-today=${endsToday}
    title=${eventTitle(context, display)}
    style=${`${options.style ?? ''};--calendar-event-span:${span}${calendarEventColorStyle(event)}${floating ? calendarDragOffsetStyle(context) : ''}`}
    @pointerdown=${(pointer: PointerEvent) =>
      startCalendarOccurrenceMovePointer(context, pointer, event, day)}
    @click=${(click: Event) => {
      click.stopPropagation();
      if ((click.currentTarget as HTMLElement).dataset.calendarSuppressClick)
        return;
      context.onSelectOccurrence(event);
    }}
    @contextmenu=${(menu: MouseEvent) =>
      context.onEventContextMenu(menu, event)}
    @keydown=${(key: KeyboardEvent) => context.onEventKeyDown(key, event)}
  >
    ${readonly || !startsToday
      ? nothing
      : html`<button
          class="${styles.dayResizeHandle}"
          data-edge="start"
          aria-label=${dataViewI18nT('calendar.accessibility.resizeStart')}
          @pointerdown=${(pointer: PointerEvent) =>
            startCalendarOccurrenceDayResizePointer(
              context,
              pointer,
              event,
              'start'
            )}
        ></button>`}
    ${readonly || !endsToday
      ? nothing
      : html`<button
          class="${styles.dayResizeHandle}"
          data-edge="end"
          aria-label=${dataViewI18nT('calendar.accessibility.resizeEnd')}
          @pointerdown=${(pointer: PointerEvent) =>
            startCalendarOccurrenceDayResizePointer(
              context,
              pointer,
              event,
              'end'
            )}
        ></button>`}
    <span class="${styles.eventTime}">
      ${display.source.allDay
        ? dataViewI18nT('calendar.grid.allDay')
        : previewing && context.mode === 'month'
          ? format(display.startAt, 'dd.MM. HH:mm')
          : format(display.startAt, 'HH:mm')}
    </span>
    ${renderEditableTitle(context, event, 'span')}
    ${renderEventMetadata(context, display.source)}
    ${event.isRecurring
      ? html`<span class="${styles.eventBadge}">R</span>`
      : invalid
        ? html`<span class="${styles.eventBadge}">!</span>`
        : event.source.invitationPreview
          ? html`<span class="${styles.eventBadge}"
              >${dataViewI18nT('calendar.event.invitationPreview')}</span
            >`
          : event.source.externalReadonly
            ? html`<span class="${styles.eventBadge}">ICS</span>`
            : nothing}
  </div>`;
}

function renderEventMetadata(
  context: CalendarGridRenderContext,
  event: CalendarEvent
): TemplateResult | typeof nothing {
  const showSource = calendarSourceIdentityVisible(
    context.displaySettings.sourceIdentityMode,
    context.calendarIds.length
  );
  const values = calendarEventDisplayValues(
    event,
    context.mode,
    context.displaySettings
  );
  if ((!showSource || !event.sourceIdentityLabel) && values.length === 0) {
    return nothing;
  }
  return html`<span class=${styles.eventMetadata} data-calendar-event-metadata>
    ${showSource && event.sourceIdentityLabel
      ? html`<span data-calendar-event-source
          >${event.sourceIdentityLabel}</span
        >`
      : nothing}
    ${values.map(
      value =>
        html`<span
          aria-label=${value.ariaLabel === value.value
            ? nothing
            : value.ariaLabel}
          data-calendar-event-property
          >${value.value}</span
        >`
    )}
  </span>`;
}

function pointerDayOffsetWithinEvent(
  target: HTMLElement,
  pointer: PointerEvent
): number {
  const span = Math.max(1, Number(target.dataset.calendarEventSpan) || 1);
  const rect = target.getBoundingClientRect();
  const ratio = rect.width ? (pointer.clientX - rect.left) / rect.width : 0;
  return Math.max(0, Math.min(span - 1, Math.floor(ratio * span)));
}

function isDraggedOccurrence(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>
): boolean {
  const dragged = context.draggedOccurrence;
  return Boolean(
    dragged &&
    dragged.source.rowId === event.source.rowId &&
    dragged.originalStartAt.getTime() === event.originalStartAt.getTime()
  );
}

function isPreviewedOccurrence(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>
): boolean {
  const preview = context.eventEditPreview;
  return Boolean(
    preview &&
    preview.rowId === event.source.rowId &&
    (preview.occurrenceStart === event.startAt.toISOString() ||
      preview.occurrenceStart === event.originalStartAt.toISOString())
  );
}

function draggedPreviewOccurrence(
  context: CalendarGridRenderContext
): CalendarOccurrence<CalendarEvent> | undefined {
  const dragged = context.draggedOccurrence;
  const preview = context.eventEditPreview;
  if (
    !dragged ||
    !preview ||
    dragged.source.rowId !== preview.rowId ||
    dragged.startAt.toISOString() !== preview.occurrenceStart
  ) {
    return undefined;
  }
  const source = {
    ...dragged.source,
    allDay: preview.allDay ?? dragged.source.allDay,
    endAt: preview.endAt,
    startAt: preview.startAt,
  };
  return {
    ...dragged,
    endAt: preview.endAt,
    occurrenceKey: `${dragged.occurrenceKey}:drag-preview`,
    source,
    startAt: preview.startAt,
  };
}

function editedPreviewOccurrence(
  context: CalendarGridRenderContext
): CalendarOccurrence<CalendarEvent> | undefined {
  const preview = context.eventEditPreview;
  if (!preview) return undefined;
  const occurrence = context
    .eventsForDay(new Date(preview.occurrenceStart))
    .find(
      item =>
        item.source.rowId === preview.rowId &&
        (item.startAt.toISOString() === preview.occurrenceStart ||
          item.originalStartAt.toISOString() === preview.occurrenceStart)
    );
  if (!occurrence) return undefined;
  return {
    ...occurrence,
    endAt: preview.endAt,
    occurrenceKey: `${occurrence.occurrenceKey}:edit-preview`,
    source: {
      ...occurrence.source,
      allDay: preview.allDay ?? occurrence.source.allDay,
      endAt: preview.endAt,
      startAt: preview.startAt,
    },
    startAt: preview.startAt,
  };
}

function draggedGhostOccurrence(
  context: CalendarGridRenderContext
):
  | { floating: boolean; occurrence: CalendarOccurrence<CalendarEvent> }
  | undefined {
  const preview = draggedPreviewOccurrence(context);
  if (preview) {
    return { floating: false, occurrence: preview };
  }
  if (!context.draggedOccurrence || !context.draggedOccurrenceOffset) {
    const edited = editedPreviewOccurrence(context);
    return edited ? { floating: false, occurrence: edited } : undefined;
  }
  return { floating: true, occurrence: context.draggedOccurrence };
}

function calendarDragOffsetStyle(context: CalendarGridRenderContext): string {
  const offset = context.draggedOccurrenceOffset;
  if (!offset) return '';
  return `;--calendar-drag-x:${offset.x}px;--calendar-drag-y:${offset.y}px`;
}

function occurrenceIntersectsDay(
  event: CalendarOccurrence<CalendarEvent>,
  day: Date
): boolean {
  return (
    event.startAt.getTime() <= endOfDay(day).getTime() &&
    event.endAt.getTime() >= startOfDay(day).getTime()
  );
}

function displayOccurrence(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>
): CalendarOccurrence<CalendarEvent> {
  const preview = context.eventEditPreview;
  if (!preview || !isPreviewedOccurrence(context, event)) return event;
  const source = {
    ...event.source,
    allDay: preview.allDay ?? event.source.allDay,
    endAt: preview.endAt,
    startAt: preview.startAt,
  };
  return {
    ...event,
    endAt: preview.endAt,
    source,
    startAt: preview.startAt,
  };
}

function commitTitleEdit(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>,
  target: HTMLElement
): void {
  const readonly = context.readonly || event.source.externalReadonly;
  if (readonly) return;
  context.onRenameOccurrence(event, target.textContent ?? '');
}

function renderEditableTitle(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>,
  tag: 'span' | 'strong'
): TemplateResult {
  const readonly = context.readonly || event.source.externalReadonly;
  const title = event.source.title || dataViewI18nT('calendar.event.untitled');
  const onPointerDown = () => {
    if (readonly) return;
  };
  const onClick = (click: MouseEvent) => {
    if (readonly) return;
    click.stopPropagation();
    const target = click.currentTarget as HTMLElement;
    if (target.closest<HTMLElement>('[data-calendar-suppress-click]')) return;
    target.focus();
  };
  const onKeyDown = (key: KeyboardEvent) => {
    if (readonly) return;
    key.stopPropagation();
    if (key.key === 'Enter') {
      key.preventDefault();
      (key.currentTarget as HTMLElement).blur();
    } else if (key.key === 'Escape') {
      key.preventDefault();
      const target = key.currentTarget as HTMLElement;
      target.textContent = title;
      target.blur();
    }
  };
  const onBlur = (blur: FocusEvent) => {
    commitTitleEdit(context, event, blur.currentTarget as HTMLElement);
  };
  const syncTitle = (element?: Element): void => {
    if (!(element instanceof HTMLElement)) return;
    if (document.activeElement === element) return;
    if (element.textContent !== title) element.textContent = title;
  };
  if (tag === 'strong') {
    return html`<strong
      class="${styles.eventTitle}"
      contenteditable=${readonly ? 'false' : 'plaintext-only'}
      spellcheck="false"
      ${ref(syncTitle)}
      @pointerdown=${onPointerDown}
      @click=${onClick}
      @keydown=${onKeyDown}
      @blur=${onBlur}
    ></strong>`;
  }
  return html`<span
    class="${styles.eventTitle}"
    contenteditable=${readonly ? 'false' : 'plaintext-only'}
    spellcheck="false"
    ${ref(syncTitle)}
    @pointerdown=${onPointerDown}
    @click=${onClick}
    @keydown=${onKeyDown}
    @blur=${onBlur}
  ></span>`;
}

function occurrenceKey(event: CalendarOccurrence<CalendarEvent>): string {
  return `${event.source.rowId}:${event.startAt.toISOString()}`;
}

function monthAnchorValue(day: Date): string {
  return `${day.getFullYear()}-${day.getMonth()}`;
}

function eventTitle(
  context: CalendarGridRenderContext,
  event: CalendarOccurrence<CalendarEvent>
): string {
  const source = event.source;
  const time = source.allDay
    ? dataViewI18nT('calendar.grid.allDay')
    : `${formatCalendarDateTime(event.startAt)} - ${formatCalendarDateTime(
        event.endAt
      )}`;
  return calendarEventAccessibleText(
    source,
    context.mode,
    context.displaySettings,
    context.calendarIds.length,
    time
  );
}

function calendarEventColorStyle(
  event: CalendarOccurrence<CalendarEvent>
): string {
  const color = event.source.color?.trim();
  return color && /^#[\da-f]{3,8}$/i.test(color)
    ? `;--calendar-event-color:${color}`
    : '';
}
